import type { ReactNode } from 'react';
import type { Tier } from '@shared/hooks/useBreakpoint';
import type { OrgRow, OrgSort } from './orgTypes';

// 26b — the organisation register.
//
// The artboard's geometry over the fields an Organization actually carries.
// That qualifier is the whole story of this file, so it is written down here
// rather than discovered again:
//
//   `Organization` in the deployed schema is
//   id · tenantId · name · domain · website · industry · size · description ·
//   logoUrl · metadata · customFields · tags · externalId · createdAt ·
//   updatedAt, plus the `customers` and `notes` relations.
//
// It has NO tier, NO status, NO parent/child hierarchy and NO ticket counts.
// The list endpoint selects exactly one aggregate, `_count.customers`. So the
// artboard's PLAN / SEATS / OPEN / BREACHED / RENEWAL / LAST CONTACT columns
// have no source at all, and six columns of em dashes is not a register — it
// is a screen that looks broken. What ships is the artboard's LAYOUT over the
// real columns, which is the call phase 23 made for the customer register and
// for the same reason.
//
// Column-for-column, what the artboard drew and what stands in its place:
//   ORGANISATION  → name + domain    (exact — the artboard's own cell)
//   PLAN          → INDUSTRY         (the real categorical on the record)
//   SEATS         → SIZE             (`size` is a BAND, "51-200", so it is
//                                     never set under a numeric head)
//   OPEN          → PEOPLE           (`_count.customers`, the one aggregate
//                                     the endpoint returns)
//   BREACHED      → TAGS
//   RENEWAL, LAST CONTACT → ADDED    (`createdAt`)
//
// Everything else is the artboard as drawn: the 28px rounded identity square,
// a name over a mono domain line, right-aligned tabular numerics, a 2px left
// rule on the row that wants attention, and the sorted column carrying the
// arrow.

/** Column widths, from the artboard's own geometry. */
const W = {
  check: 34,
  industry: 130,
  size: 110,
  people: 104,
  tags: 150,
  added: 96,
  menu: 34,
} as const;

/**
 * Which optional columns survive at this tier.
 *
 * Drop order: TAGS first, then SIZE, then ADDED, then INDUSTRY collapses under
 * the organisation name. ORGANISATION and PEOPLE never drop — an organisation
 * register with no name and no headcount is not a register.
 */
export function columnsFor(tier: Tier) {
  return {
    tags: tier === 'full',
    size: tier === 'full' || tier === 'laptop',
    added: tier !== 'tabletP',
    industry: tier !== 'tabletP',
  };
}

const MONO = 'font-mono';

/**
 * The identity square's fill.
 *
 * Neutral only. `identityColor` (tickets) rotates through four HUES, one of
 * which is burgundy — on a person that is decorative, but on an account row it
 * would read as the account's condition. So organisations rotate through four
 * greys, exactly as the artboard draws them.
 */
const ORG_FILLS = [
  'var(--co-ink-2)',
  'var(--co-ink-4)',
  'var(--co-ink-3)',
  'var(--co-ink)',
] as const;

export function orgFill(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return ORG_FILLS[hash % ORG_FILLS.length];
}

/** The artboard shows one glyph in the square, not two. */
export function orgInitial(name: string): string {
  return (name.trim()[0] ?? '·').toUpperCase();
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function ColumnHead({
  children,
  width,
  align = 'left',
  emphasis,
  className = '',
}: {
  children?: ReactNode;
  width?: number;
  align?: 'left' | 'right';
  /** The sorted column: the artboard darkens its head rather than adding a rule. */
  emphasis?: boolean;
  className?: string;
}) {
  return (
    <div
      role="columnheader"
      className={`${MONO} ${width ? 'flex-none' : 'min-w-0 flex-1'} text-[9.5px] font-semibold uppercase tracking-[.2em] ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
      style={{ width, color: emphasis ? 'var(--co-ink)' : 'var(--co-ink-3)' }}
    >
      {children}
    </div>
  );
}

export function OrgRegisterHead({
  tier,
  allSelected,
  onSelectAllOnPage,
  sortBy,
  sortOrder,
}: {
  tier: Tier;
  allSelected: boolean;
  onSelectAllOnPage: (checked: boolean) => void;
  sortBy: OrgSort;
  sortOrder: 'asc' | 'desc';
}) {
  const cols = columnsFor(tier);
  const arrow = sortOrder === 'asc' ? ' ↑' : ' ↓';
  const on = (key: OrgSort) => (sortBy === key ? arrow : '');

  return (
    <div
      role="row"
      className="flex flex-none items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px]"
    >
      <div className="flex flex-none items-center" style={{ width: W.check }}>
        {/* This box means THIS PAGE and nothing else. */}
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onSelectAllOnPage(e.target.checked)}
          aria-label="Select every organisation on this page"
          title="Selects this page only"
          className="h-[14px] w-[14px] rounded-[3px] border border-[var(--co-line)] accent-[var(--co-brass)]"
        />
      </div>

      <ColumnHead emphasis={sortBy === 'name'}>{`ORGANISATION${on('name')}`}</ColumnHead>
      {cols.industry && (
        <ColumnHead width={W.industry} emphasis={sortBy === 'industry'}>
          {`INDUSTRY${on('industry')}`}
        </ColumnHead>
      )}
      {cols.size && <ColumnHead width={W.size}>SIZE</ColumnHead>}
      <ColumnHead width={W.people} align="right" className="pr-4">
        PEOPLE
      </ColumnHead>
      {cols.tags && <ColumnHead width={W.tags}>TAGS</ColumnHead>}
      {cols.added && (
        <ColumnHead
          width={W.added}
          align="right"
          emphasis={sortBy === 'createdAt'}
          className="pr-5"
        >
          {`ADDED${on('createdAt')}`}
        </ColumnHead>
      )}
      <ColumnHead width={W.menu} />
    </div>
  );
}

export function OrgRegisterRow({
  row,
  tier,
  selected,
  onToggle,
  onOpen,
  onMenu,
}: {
  row: OrgRow;
  tier: Tier;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const cols = columnsFor(tier);
  // The only row-level emphasis on the screen, and it is earned: every ticket
  // in the product routes through a CUSTOMER, so an organisation no customer
  // record points at is invisible to the rest of the system. That is the one
  // problem this register can actually prove from the data it has.
  const unlinked = row.people === 0;

  return (
    <div
      role="row"
      className="flex items-center border-b border-l-2 border-[var(--co-line-subtle)] px-5 py-[13px] hover:bg-[var(--co-selected)]"
      style={{ borderLeftColor: unlinked ? 'var(--co-terracotta-line)' : 'transparent' }}
    >
      <div className="flex flex-none items-center" style={{ width: W.check }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Select ${row.name}`}
          className="h-[14px] w-[14px] rounded-[3px] border border-[var(--co-line)] accent-[var(--co-brass)]"
        />
      </div>

      {/* ORGANISATION */}
      <div className="flex min-w-0 flex-1 items-center gap-[11px] pr-4">
        <span
          aria-hidden
          className="flex h-[28px] w-[28px] flex-none items-center justify-center rounded-[7px] text-[11px] font-semibold text-[var(--co-ink-invert)]"
          style={{ background: orgFill(row.name) }}
        >
          {orgInitial(row.name)}
        </span>
        <div className="min-w-0">
          <button
            type="button"
            onClick={onOpen}
            className="block min-w-0 max-w-full truncate text-left text-[13.5px] font-semibold text-[var(--co-ink)] hover:underline"
          >
            {row.name}
          </button>
          {/* The domain is the join key — it is how an inbound address would be
              matched to this account — so it sits under the name rather than
              in a column of its own, exactly as the artboard has it. */}
          <div className={`${MONO} mt-[3px] truncate text-[10.5px] text-[var(--co-ink-3)]`}>
            {row.domain ?? 'no domain'}
          </div>
          {!cols.industry && row.industry && (
            <div
              className={`${MONO} mt-[3px] truncate text-[10px] uppercase tracking-[.08em] text-[var(--co-ink-4)]`}
            >
              {row.industry}
            </div>
          )}
        </div>
      </div>

      {/* INDUSTRY */}
      {cols.industry && (
        <div
          className="min-w-0 flex-none truncate text-[12.5px] text-[var(--co-ink-2)]"
          style={{ width: W.industry }}
        >
          {row.industry ?? <span className="text-[var(--co-line-strong)]">—</span>}
        </div>
      )}

      {/* SIZE — a band, never rendered as though it were a headcount. */}
      {cols.size && (
        <div className={`${MONO} min-w-0 flex-none truncate text-[12px]`} style={{ width: W.size }}>
          {row.size ? (
            <span className="text-[var(--co-ink-2)]">{row.size}</span>
          ) : (
            <span className="text-[var(--co-line-strong)]">—</span>
          )}
        </div>
      )}

      {/* PEOPLE — `_count.customers`, the one aggregate the list returns. */}
      <div className="flex-none pr-4 text-right" style={{ width: W.people }}>
        <span
          className={`${MONO} text-[13px] font-semibold tabular-nums`}
          style={{ color: unlinked ? 'var(--co-terracotta)' : 'var(--co-ink)' }}
        >
          {row.people}
        </span>
        {unlinked && (
          <div
            className={`${MONO} mt-[4px] text-[9.5px] uppercase tracking-[.08em] text-[var(--co-ink-4)]`}
          >
            unlinked
          </div>
        )}
      </div>

      {/* TAGS */}
      {cols.tags && (
        <div className="flex flex-none flex-wrap items-center gap-1" style={{ width: W.tags }}>
          {row.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className={`${MONO} rounded-[5px] border border-[var(--co-line)] px-[6px] py-[3px] text-[9.5px] font-medium uppercase tracking-[.08em] text-[var(--co-ink-3)]`}
            >
              {tag}
            </span>
          ))}
          {row.tags.length > 2 && (
            <span className={`${MONO} text-[9.5px] text-[var(--co-ink-4)]`}>
              {`+${row.tags.length - 2}`}
            </span>
          )}
          {row.tags.length === 0 && (
            <span className={`${MONO} text-[11px] text-[var(--co-line-strong)]`}>—</span>
          )}
        </div>
      )}

      {/* ADDED */}
      {cols.added && (
        <div
          className={`${MONO} flex-none pr-5 text-right text-[11.5px] tabular-nums text-[var(--co-ink-2)]`}
          style={{ width: W.added }}
        >
          {shortDate(row.createdAt)}
        </div>
      )}

      <div className="flex flex-none justify-center" style={{ width: W.menu }}>
        <button
          type="button"
          onClick={onMenu}
          aria-label={`Actions for ${row.name}`}
          className="text-[13px] leading-none text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
        >
          ⋯
        </button>
      </div>
    </div>
  );
}
